import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react"

import type { SavedTableView } from "~/server/api/routers/tableView"

import type { Store } from "./types"
import type { CartItemWithLines } from "./useAllocationData"

/** Shared actions the allocation-planning layout exposes to its tab routes —
 * the cart, overlays, and dialogs live in the layout so they survive tab
 * navigation. */
export type AllocationPlanningContextValue = {
  addToCart: (items: CartItemWithLines[]) => void
  openStoreDetail: (store: Store) => void
}

const AllocationPlanningContext = createContext<AllocationPlanningContextValue | null>(null)

export const AllocationPlanningProvider = AllocationPlanningContext.Provider

export function useAllocationPlanning(): AllocationPlanningContextValue {
  const value = useContext(AllocationPlanningContext)
  if (value === null) {
    throw new Error("useAllocationPlanning must be used inside the allocation-planning layout")
  }
  return value
}

/** Saved-view page ids: each allocation table keeps its own views. */
export const REPLENISHMENT_VIEW_PAGE = "allocationReplenishment"
export const PRODUCT_REPORT_VIEW_PAGE = "allocationProductReport"
export const STORE_REPORT_VIEW_PAGE = "allocationStoreReport"

/** The saved view in use on each allocation table, by saved-view page id. Each
 * table has its own views and keeps its active one while you visit other tabs. */
type AllocationViewContextValue = {
  activeViews: Readonly<Record<string, SavedTableView | null>>
  setActiveView: (page: string, view: SavedTableView | null) => void
}

const AllocationViewContext = createContext<AllocationViewContextValue | null>(null)

export function AllocationViewProvider({ children }: { children: ReactNode }) {
  const [activeViews, setActiveViews] = useState<Record<string, SavedTableView | null>>({})
  const setActiveView = useCallback(
    (page: string, view: SavedTableView | null) =>
      setActiveViews((current) => ({ ...current, [page]: view })),
    [],
  )
  const value = useMemo(() => ({ activeViews, setActiveView }), [activeViews, setActiveView])
  return <AllocationViewContext.Provider value={value}>{children}</AllocationViewContext.Provider>
}

/** The active saved view of one table (`page` is its saved-view page id). */
export function useAllocationView(page: string): {
  activeView: SavedTableView | null
  setActiveView: (view: SavedTableView | null) => void
} {
  const value = useContext(AllocationViewContext)
  if (value === null) {
    throw new Error("useAllocationView must be used inside the allocation layout")
  }
  const { activeViews, setActiveView } = value
  return useMemo(
    () => ({
      activeView: activeViews[page] ?? null,
      setActiveView: (view: SavedTableView | null) => setActiveView(page, view),
    }),
    [activeViews, page, setActiveView],
  )
}
